import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Inbox, PhoneCall, CalendarClock, FileText, CheckCircle2, Archive, Layers, ClipboardList, AlertTriangle, Gauge } from "lucide-react";
import { api } from "../../lib/api";

const cards = [
  { key: "neu", label: "Neue Anfragen", icon: Inbox },
  { key: "offen", label: "Offen / unbearbeitet", icon: ClipboardList },
  { key: "rueckrufe", label: "Rückrufe", icon: PhoneCall },
  { key: "besichtigungen", label: "Besichtigungen", icon: CalendarClock },
  { key: "angebote", label: "Angebote", icon: FileText },
  { key: "beauftragt", label: "Beauftragt", icon: Layers },
  { key: "abgeschlossen", label: "Abgeschlossen", icon: CheckCircle2 },
  { key: "archiviert", label: "Archiviert", icon: Archive },
];

const statusColor = (s) => {
  if (s === "Neu") return "bg-yellow text-brand-black";
  if (["Abgeschlossen"].includes(s)) return "bg-green-600 text-white";
  if (["Abgelehnt", "Storniert"].includes(s)) return "bg-red-500 text-white";
  return "bg-dark-gray text-white";
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [attention, setAttention] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([api.get("/admin/stats"), api.get("/admin/inquiries")]);
        setStats(s.data);
        setRecent(r.data.slice(0, 8));
        try {
          const v = await api.get("/admin/vitals", { params: { days: 30 } });
          const core = ["LCP", "INP", "CLS"];
          const bad = (v.data.pages || [])
            .filter((p) => p.path.startsWith("/entruempelung/"))
            .map((p) => {
              const weak = core.filter((n) => p.metrics[n] && p.metrics[n].rating !== "good");
              return { ...p, weak };
            })
            .filter((p) => p.weak.length > 0)
            .sort((a, b) => b.weak.length - a.weak.length)
            .slice(0, 6);
          setAttention(bad);
        } catch {
          /* vitals optional */
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl font-bold text-anthracite">Dashboard</h1>
      <p className="mt-1 text-mid-gray">Übersicht der aktuellen Vorgänge</p>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.key} className="rounded-md border border-light-gray bg-white p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-light-gray text-anthracite">
                <c.icon className="h-5 w-5" />
              </span>
              <span className="font-heading text-3xl font-extrabold text-anthracite">{stats?.[c.key] ?? 0}</span>
            </div>
            <p className="mt-3 text-sm font-semibold text-mid-gray">{c.label}</p>
          </div>
        ))}
      </div>

      {attention.length > 0 && (
        <div className="mt-8 rounded-md border border-amber-300 bg-amber-50 p-5" data-testid="dashboard-vitals-attention">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-anthracite">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Ortsseiten mit Optimierungsbedarf
            </h2>
            <Link to="/admin/performance" className="inline-flex items-center gap-1 font-heading text-sm font-bold text-anthracite hover:text-yellow-dark">
              <Gauge className="h-4 w-4" /> Performance
            </Link>
          </div>
          <p className="mt-1 text-sm text-mid-gray">Schwache Core Web Vitals (p75, letzte 30 Tage) – diese Seiten sollten Sie im Blick behalten.</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {attention.map((p) => (
              <Link key={p.path} to="/admin/performance" className="rounded-sm border border-amber-200 bg-white p-3 transition-colors hover:border-amber-400" data-testid={`attention-${p.path}`}>
                <p className="truncate text-sm font-semibold text-anthracite">{p.path}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.weak.map((n) => (
                    <span key={n} className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${p.metrics[n].rating === "poor" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                      {n} {p.metrics[n].rating === "poor" ? "schwach" : "mittel"}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 rounded-md border border-light-gray bg-white">
        <div className="flex items-center justify-between border-b border-light-gray p-5">
          <h2 className="font-heading text-lg font-bold text-anthracite">Zuletzt eingegangen</h2>
          <Link to="/admin/anfragen" className="font-heading text-sm font-bold text-anthracite hover:text-yellow-dark">Alle Anfragen</Link>
        </div>
        {recent.length === 0 ? (
          <p className="p-8 text-center text-mid-gray" data-testid="dashboard-empty">Aktuell liegen keine neuen Anfragen vor.</p>
        ) : (
          <ul className="divide-y divide-light-gray">
            {recent.map((i) => (
              <li key={i.id}>
                <Link to={`/admin/anfragen/${i.id}`} className="flex items-center justify-between gap-4 p-5 transition-colors hover:bg-light-gray" data-testid={`recent-${i.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-heading font-bold text-anthracite">{i.name} · {i.location}</p>
                    <p className="truncate text-sm text-mid-gray">{i.number} · {i.service}</p>
                  </div>
                  <span className={`shrink-0 rounded-sm px-2.5 py-1 text-xs font-bold ${statusColor(i.status)}`}>{i.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
